import {
  Box,
  Grid,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from "@mui/material"
import Header from "components/Header/Header"
import ProfileDisplayer from "components/ProfileDisplayer"
import AppPagination from "components/StyledComponents/AppPagination"
import ToggleSwitch from "components/StyledComponents/ToggleSwitch"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import { vipPermissionColumns } from "helpers/constant"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { getNotableUsersAction } from "redux/superAdmin/actions"
import { updateNotableUsersAction } from "redux/superAdmin/actions/user"
import SearchComponent from "components/SearchComponent"
import { useDebounce } from "hooks/utils"

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "12px 24px 12px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": {
      paddingRight: 0
    }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid ${theme.palette.drawer.main}`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid ${theme.palette.drawer.main}`,
    "&:last-child": {
      paddingRight: 0
    }
  }
}))
const VipPermission = () => {
  const [breadCrumbs, setBreadCrumbs] = useState([])
  const theme = useTheme()
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(1)
  const { isLoading, filters } = useSelector((store) => store?.app)
  const { searchField } = filters || {}
  const { notableUsers } = useSelector((store) => store?.superAdmin)
  const debouncedSearchTerm = useDebounce(searchField, 500)

  useEffect(() => {
    document.title = "VIP Permissions"
    setBreadCrumbs(createBreadcrumbs())
    dispatch(getNotableUsersAction())
    setCurrentPage(1)
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm])

  const itemsPerPage = 12
  const filteredUsersList =
    notableUsers?.filter((user) => {
      const term = debouncedSearchTerm?.toLowerCase()
      return user?.name?.toLowerCase().includes(term) || user?.email?.toLowerCase().includes(term)
    }) ?? []
  const totalPages = Math.ceil(filteredUsersList?.length / itemsPerPage)

  const paginatedUsersList = filteredUsersList?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )
  const handleApprovalStatusChange = (id, approval) => {
    dispatch(
      updateNotableUsersAction({
        user_ids: [id],
        has_access: approval ? true : false
      })
    )
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Header title="VIP Permissions" breadCrumbs={breadCrumbs}>
          <Box
            sx={{
              marginRight: "20px"
            }}>
            <SearchComponent />
          </Box>
        </Header>
      </Grid>
      <Grid item xs={12}>
        <TableContainer sx={{ maxHeight: 700 }}>
          <CustomTable>
            <TableHead>
              <TableRow>
                {vipPermissionColumns?.map((column) => (
                  <TableCell key={column.id} sx={{ minWidth: column.minWidth }}>
                    <Typography variant="body2Regular" color={theme.palette.text.secondary}>
                      {column.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsersList?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={vipPermissionColumns?.length} align="center">
                    {isLoading ? "Please wait we are getting the users..." : "No Data Available"}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsersList?.map((row, index) => (
                  <TableRow key={row.id || index}>
                    <TableCell>{currentPage * 12 - (12 - 1) + index}</TableCell>
                    <TableCell>
                      <Box className="d-flex align-items-center gap-1">
                        <ProfileDisplayer image={row?.image} />
                        <Typography variant="body1Regular"> {row?.name ?? ""}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{row?.role ?? ""}</TableCell>
                    <TableCell>{row?.email ?? ""}</TableCell>
                    <TableCell>
                      <ToggleSwitch
                        checked={row?.approval}
                        onChange={(e) =>
                          handleApprovalStatusChange(row.id, e.target.checked, row?.approval)
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </CustomTable>
        </TableContainer>
        {paginatedUsersList?.length > 0 && (
          <Box className="d-flex justify-content-end mt-3">
            <AppPagination
              totalPages={totalPages}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          </Box>
        )}
      </Grid>
    </Grid>
  )
}

export default VipPermission
